import { BadgeName } from '@/features/player-session/types/types';

/**
 * Deep links into the Player Pathway magazines, per level + badge, from the "UPDATED PATHWAY"
 * sheet. Levels 6–10 are deliberately absent — nothing is published for them yet, so callers fall
 * back to the generic link.
 */

const BOOK_LEVEL_1_3 =
  'https://viewer.joomag.com/2026-player-pathway-level-1-3/0371503001697045141';
const BOOK_LEVEL_4_5 =
  'https://viewer.joomag.com/2026-player-pathway-level-4-5/0510542001772572274';

/** Levels 1–3 share one magazine, 4–5 the other; only the page differs. */
const buildUrl = (level: number, page: number) =>
  `${level <= 3 ? BOOK_LEVEL_1_3 : BOOK_LEVEL_4_5}/p${page}?short=`;

/** `forms` backs the "Put Your Forms to Work" link, `skills` the Skill rows. */
export type PathwayMaterialKind = 'forms' | 'skills';

/**
 * Magazine URL for a level, without a page anchor. Used where the exact page can't be resolved
 * (the step panel knows the level but not the badge).
 */
export function getPathwayMaterialBookUrl(level: number): string | null {
  if (!PAGES[level]) return null;
  return level <= 3 ? BOOK_LEVEL_1_3 : BOOK_LEVEL_4_5;
}

/**
 * Strips the scheme for on-screen text. The "Please see …" copy has always shown the bare host +
 * path (`foundation.uskidsgolf.com/L1-3`) while opening the `https://` URL — keep that split.
 */
export function toPathwayMaterialDisplay(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

type BadgePages = {
  /** Scoring and Knowledge have no Forms page — those fall back to `skills`. */
  forms?: number;
  skills: number;
};

/**
 * Pages per level → badge. The sheet is badge-level, so where the sheet lists several checkpoints
 * under one badge we use that badge's primary rows:
 *  - `aroundthegreen` → the "Around the Green" rows ("| Sand" is a sub-checkpoint the sheet
 *    doesn't distinguish).
 *  - `fullswing` → "Full Swing" forms + "Full Swing | Driver" skills.
 */
const PAGES: Record<number, Partial<Record<BadgeName, BadgePages>>> = {
  1: {
    [BadgeName.Putting]: { forms: 6, skills: 10 },
    [BadgeName.Aroundthegreen]: { forms: 14, skills: 18 },
    [BadgeName.Fullswing]: { forms: 24, skills: 28 },
    [BadgeName.Mastery]: { skills: 32 },
    [BadgeName.Knowledge]: { skills: 34 },
  },
  2: {
    [BadgeName.Putting]: { forms: 8, skills: 10 },
    [BadgeName.Aroundthegreen]: { forms: 16, skills: 18 },
    [BadgeName.Fullswing]: { forms: 26, skills: 28 },
    [BadgeName.Mastery]: { skills: 32 },
    [BadgeName.Knowledge]: { skills: 36 },
  },
  3: {
    [BadgeName.Putting]: { forms: 8, skills: 10 },
    [BadgeName.Aroundthegreen]: { forms: 16, skills: 18 },
    [BadgeName.Fullswing]: { forms: 26, skills: 28 },
    [BadgeName.Mastery]: { skills: 32 },
    [BadgeName.Knowledge]: { skills: 38 },
  },
  4: {
    [BadgeName.Putting]: { forms: 6, skills: 8 },
    [BadgeName.Aroundthegreen]: { forms: 12, skills: 14 },
    [BadgeName.Fullswing]: { forms: 24, skills: 26 },
    [BadgeName.Mastery]: { skills: 30 },
    [BadgeName.Knowledge]: { skills: 32 },
  },
  5: {
    [BadgeName.Putting]: { forms: 6, skills: 8 },
    [BadgeName.Aroundthegreen]: { forms: 12, skills: 14 },
    [BadgeName.Fullswing]: { forms: 24, skills: 26 },
    [BadgeName.Mastery]: { skills: 30 },
    [BadgeName.Knowledge]: { skills: 34 },
  },
};

/**
 * Resolves the magazine link for a level + badge, or `null` when nothing is published for that
 * combination (levels 6–10, or an unknown badge) so the caller can fall back.
 */
export function getPathwayMaterialLink(
  level: number,
  badge: BadgeName | null | undefined,
  kind: PathwayMaterialKind,
): string | null {
  if (!badge) return null;

  const pages = PAGES[level]?.[badge];
  if (!pages) return null;

  return buildUrl(level, kind === 'forms' ? (pages.forms ?? pages.skills) : pages.skills);
}
